import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { Context } from '../../../App';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import SeoHelmet from '../../common/SeoHelmet';
import MuiSnackbar from '../../common/MuiSnackbar';
import Sidebar from '../../sidebar/Sidebar';
import DecisionModal from '../../modals/DecisionModal';
import CalculateButton from '../../common/calculateButton';
import SaveButton from '../../common/saveButton';
import { useCalculationContext, CalculationContextProvider } from '../../context/calculationContext';
import useLoadSavedCalculation from '../useLoadSavedCalculation';
import SlopeStabilitySketch from './SlopeStabilitySketch';

const DEF_PROFILE = [
  { x: '0', y: '12' },
  { x: '10', y: '12' },
  { x: '30', y: '0' },
  { x: '45', y: '0' },
];
const DEF_MATERIAL = { name: 'Soil-1', cPrime: '10', phiPrime: '38', gammaDry: '18', gammaSat: '20' };
const DEF_WATER = { elevation: '4', gammaWater: '9.81' };
const DEF_SINGLE = { centerX: '21.72', centerY: '13.6', radius: '15.884' };
const DEF_SEARCH = {
  centerX: { min: '8', max: '35', count: '8' },
  centerY: { min: '8', max: '35', count: '8' },
  radius: { min: '8', max: '40', count: '10' },
};
const DEF_OPTS = {
  tolerance: '0.0001',
  maxIterations: '100',
  maxReportedCandidates: '10',
  minValidMAlpha: '0.2',
  bishopClosureModel: 'n_force_calibrated',
  debugSlices: false,
};

const computeProfileBounds = (points) => {
  if (!Array.isArray(points)) return null;
  const finitePoints = points
    .map((point) => ({ x: Number(point?.x), y: Number(point?.y) }))
    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
  if (finitePoints.length < 2) return null;
  return {
    minX: Math.min(...finitePoints.map((point) => point.x)),
    maxX: Math.max(...finitePoints.map((point) => point.x)),
    minY: Math.min(...finitePoints.map((point) => point.y)),
    maxY: Math.max(...finitePoints.map((point) => point.y)),
  };
};

const createSurfaceFiltersFromBounds = (bounds, allowReverseCurvature = false) => ({
  // Keep empty by default: empty means "auto from profile min Y".
  minArcElevation: '',
  leftLimitX: Number.isFinite(bounds?.minX) ? String(bounds.minX) : '',
  rightLimitX: Number.isFinite(bounds?.maxX) ? String(bounds.maxX) : '',
  failureDirection: 'auto',
  allowReverseCurvature: Boolean(allowReverseCurvature),
});

const DEF_SURFACE_FILTERS = createSurfaceFiltersFromBounds(computeProfileBounds(DEF_PROFILE), false);
const MAX_MATERIAL_NAME_LENGTH = 60;
const MIN_GROUND_PROFILE_POINTS = 2;
const MAX_GROUND_PROFILE_POINTS = 50;
const MIN_SLICE_COUNT = 10;
const MAX_SLICE_COUNT = 150;
const SEARCH_CENTER_ABS_MAX = 10000;
const SEARCH_RADIUS_MAX = 10000;
const SEARCH_COUNT_MIN = 1;
const SEARCH_COUNT_MAX = 40;
const SEARCH_TOTAL_CANDIDATES_MAX = 20000;
const OPTIONS_TOLERANCE_MIN = 1e-8;
const OPTIONS_TOLERANCE_MAX = 1e-2;
const OPTIONS_MAX_ITERATIONS_MIN = 20;
const OPTIONS_MAX_ITERATIONS_MAX = 500;
const OPTIONS_MAX_REPORTED_CANDIDATES_MIN = 1;
const OPTIONS_MAX_REPORTED_CANDIDATES_MAX = 50;
const OPTIONS_MIN_VALID_M_ALPHA_MIN = 0.05;
const OPTIONS_MIN_VALID_M_ALPHA_MAX = 0.95;
const HEATMAP_PALETTE_OPTIONS = [
  { key: 'default', label: 'Default' },
  { key: 'colorblind', label: 'Colorblind-safe' },
];
const METHOD_OPTIONS = [
  { key: 'bishop_simplified', label: 'Bishop Simplified', isImplemented: true },
  { key: 'janbu_simplified', label: 'Janbu Simplified (Stub)', isImplemented: false },
];
const FAILURE_DIRECTION_OPTIONS = [
  { key: 'auto', label: 'Auto' },
  { key: 'left_to_right', label: 'Left to Right' },
  { key: 'right_to_left', label: 'Right to Left' },
];
const getMethodLabel = (key) => METHOD_OPTIONS.find((option) => option.key === key)?.label || key;
const normalizeFailureDirection = (value) => (FAILURE_DIRECTION_OPTIONS.some((option) => option.key === value) ? value : 'auto');
const getFailureDirectionLabel = (value) =>
  FAILURE_DIRECTION_OPTIONS.find((option) => option.key === normalizeFailureDirection(value))?.label || 'Auto';
const normalizeHeatmapPalette = (value) => (HEATMAP_PALETTE_OPTIONS.some((option) => option.key === value) ? value : 'default');

const num = (v) => {
  if (v === '' || v === null || v === undefined) return Number.NaN;
  const x = Number(v);
  return Number.isFinite(x) ? x : Number.NaN;
};
const intNum = (v) => {
  if (v === '' || v === null || v === undefined) return Number.NaN;
  const x = Number.parseInt(v, 10);
  return Number.isInteger(x) ? x : Number.NaN;
};
const fixed = (v, d = 3) => (Number.isFinite(Number(v)) ? Number(v).toFixed(d) : '-');
const labelWithHelp = (labelText, helpText) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.375,
      px: 0.35,
      py: 0.05,
      mr: 0.25,
      borderRadius: 0.5,
      backgroundColor: 'background.paper',
      whiteSpace: 'nowrap',
      lineHeight: 1.1,
      position: 'relative',
      zIndex: 1,
    }}
  >
    <Box component="span" sx={{ lineHeight: 1.1 }}>
      {labelText}
    </Box>
    <Tooltip title={helpText} arrow placement="top" enterTouchDelay={0}>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
        <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
      </Box>
    </Tooltip>
  </Box>
);

const SlopeStability = () => {
  const { theme: appTheme } = useContext(Context);
  const {
    MAX_NAME_LENGTH,
    isLoadingCalculate,
    isLoadingLoadAll,
    isLoadingLoad,
    isLoadingSave,
    result,
    calculations,
    showOutput,
    setShowOutput,
    calculateButtonDisabled,
    setCalculateButtonDisabled,
    saveButtonDisabled,
    setSaveButtonDisabled,
    errorMessage,
    displaySidebar,
    setDisplaySidebar,
    decisionModalIsOpen,
    decision,
    setDecision,
    handleSidebarDisplay,
    setDecisionModalIsOpenToFalse,
    showMuiSnack,
    open,
    setOpen,
    alertMessage,
    severity,
    calculate,
    loadOneCalculation,
    loadAllCalculations,
    update,
    checkCalculationNameforSaving,
    checkCalculationNameforSavingAs,
    deleteCalculations,
    calculationCountReached,
    saveCountReached,
  } = useCalculationContext();

  const [name, setName] = useState('Case-01');
  const [sliceCount, setSliceCount] = useState('24');
  const [mode, setMode] = useState('search');
  const [profile, setProfile] = useState(DEF_PROFILE);
  const [material, setMaterial] = useState(DEF_MATERIAL);
  const [water, setWater] = useState(DEF_WATER);
  const [single, setSingle] = useState(DEF_SINGLE);
  const [search, setSearch] = useState(DEF_SEARCH);
  const [surfaceFilters, setSurfaceFilters] = useState({ ...DEF_SURFACE_FILTERS });
  const [opts, setOpts] = useState(DEF_OPTS);
  const [inputTabValue, setInputTabValue] = useState(0);
  const [selectedMethods, setSelectedMethods] = useState(['bishop_simplified']);
  const [heatmapPalette, setHeatmapPalette] = useState('default');

  const URL = {
    CALCULATE: '/api/v1/calculations/slopeStability',
    LOADONE: '/api/v1/calculations/slopeStability/loadOne/',
    LOADALL: '/api/v1/calculations/slopeStability/loadAll',
    UPDATE: '/api/v1/calculations/slopeStability/update/',
    SAVE: '/api/v1/calculations/slopeStability/save',
    CHECKUNIQUENAME: '/api/v1/calculations/slopeStability/checkUniqueName/',
    DELETE: '/api/v1/calculations/slopeStability/delete',
  };

  const setInputs = (loadedInput) => {
    const src = loadedInput?.input || loadedInput || {};
    const methodsFromInput = Array.isArray(src.methods)
      ? src.methods.map((methodKey) => String(methodKey))
      : src.method
        ? [String(src.method)]
        : ['bishop_simplified'];
    const loadedProfile =
      Array.isArray(src.groundProfile) && src.groundProfile.length > 1
        ? src.groundProfile.map((p) => ({ x: String(p?.x ?? ''), y: String(p?.y ?? '') }))
        : DEF_PROFILE;
    const loadedBounds = computeProfileBounds(loadedProfile);
    const defaultLoadedSurfaceFilters = createSurfaceFiltersFromBounds(loadedBounds, false);
    const loadedMinArcElevationRaw = src.surfaceFilters?.minArcElevation;
    const loadedMinArcElevationNum = num(loadedMinArcElevationRaw);
    const isLoadedMinArcAuto =
      loadedMinArcElevationRaw === undefined ||
      !Number.isFinite(loadedMinArcElevationNum) ||
      (Number.isFinite(loadedBounds?.minY) && Math.abs(loadedMinArcElevationNum - loadedBounds.minY) <= 1e-6);

    setName(String(src.name ?? ''));
    setSliceCount(String(src.sliceCount ?? ''));
    setSelectedMethods(methodsFromInput.length > 0 ? methodsFromInput : ['bishop_simplified']);
    setProfile(loadedProfile);
    setMaterial({
      name: String(src.material?.name ?? DEF_MATERIAL.name),
      cPrime: String(src.material?.cPrime ?? DEF_MATERIAL.cPrime),
      phiPrime: String(src.material?.phiPrime ?? DEF_MATERIAL.phiPrime),
      gammaDry: String(src.material?.gammaDry ?? DEF_MATERIAL.gammaDry),
      gammaSat: String(src.material?.gammaSat ?? DEF_MATERIAL.gammaSat),
    });
    setWater({
      elevation: String(src.waterTable?.elevation ?? DEF_WATER.elevation),
      gammaWater: String(src.waterTable?.gammaWater ?? DEF_WATER.gammaWater),
    });
    setSingle({
      centerX: String(src.singleSurface?.centerX ?? DEF_SINGLE.centerX),
      centerY: String(src.singleSurface?.centerY ?? DEF_SINGLE.centerY),
      radius: String(src.singleSurface?.radius ?? DEF_SINGLE.radius),
    });
    setSearch({
      centerX: {
        min: String(src.search?.centerX?.min ?? DEF_SEARCH.centerX.min),
        max: String(src.search?.centerX?.max ?? DEF_SEARCH.centerX.max),
        count: String(src.search?.centerX?.count ?? DEF_SEARCH.centerX.count),
      },
      centerY: {
        min: String(src.search?.centerY?.min ?? DEF_SEARCH.centerY.min),
        max: String(src.search?.centerY?.max ?? DEF_SEARCH.centerY.max),
        count: String(src.search?.centerY?.count ?? DEF_SEARCH.centerY.count),
      },
      radius: {
        min: String(src.search?.radius?.min ?? DEF_SEARCH.radius.min),
        max: String(src.search?.radius?.max ?? DEF_SEARCH.radius.max),
        count: String(src.search?.radius?.count ?? DEF_SEARCH.radius.count),
      },
    });
    setSurfaceFilters({
      // Treat values equal to profile min-Y as "auto" so the UI remains clear.
      minArcElevation: isLoadedMinArcAuto ? '' : String(src.surfaceFilters.minArcElevation),
      // Legacy fallback mapping: entryX.min -> leftLimitX, exitX.max -> rightLimitX.
      leftLimitX:
        src.surfaceFilters?.leftLimitX !== undefined
          ? String(src.surfaceFilters.leftLimitX)
          : src.surfaceFilters?.entryX?.min !== undefined
            ? String(src.surfaceFilters.entryX.min)
            : defaultLoadedSurfaceFilters.leftLimitX,
      rightLimitX:
        src.surfaceFilters?.rightLimitX !== undefined
          ? String(src.surfaceFilters.rightLimitX)
          : src.surfaceFilters?.exitX?.max !== undefined
            ? String(src.surfaceFilters.exitX.max)
            : defaultLoadedSurfaceFilters.rightLimitX,
      failureDirection: normalizeFailureDirection(src.surfaceFilters?.failureDirection),
      allowReverseCurvature:
        src.surfaceFilters?.allowReverseCurvature !== undefined ? Boolean(src.surfaceFilters.allowReverseCurvature) : false,
    });
    setOpts({
      tolerance: String(src.analysisOptions?.tolerance ?? DEF_OPTS.tolerance),
      maxIterations: String(src.analysisOptions?.maxIterations ?? DEF_OPTS.maxIterations),
      maxReportedCandidates: String(src.analysisOptions?.maxReportedCandidates ?? DEF_OPTS.maxReportedCandidates),
      minValidMAlpha: String(src.analysisOptions?.minValidMAlpha ?? DEF_OPTS.minValidMAlpha),
      bishopClosureModel: DEF_OPTS.bishopClosureModel,
      debugSlices:
        src.analysisOptions?.debugSlices !== undefined ? Boolean(src.analysisOptions.debugSlices) : DEF_OPTS.debugSlices,
    });
    setMode(src.singleSurface ? 'single' : 'search');
    setHeatmapPalette('default');
  };

  useLoadSavedCalculation({ URL, loadOneCalculation, calculate, setInputs, showMuiSnack });

  const numberInputProps = useMemo(
    () => ({
      style: { colorScheme: appTheme === 'dark' ? 'dark' : 'light' },
    }),
    [appTheme],
  );
  const mergeNumberInputProps = (inputProps = {}) => ({
    ...inputProps,
    style: {
      ...numberInputProps.style,
      ...(inputProps?.style || {}),
    },
  });

  const calculationInput = useMemo(() => {
    const normalizedSelectedMethods = selectedMethods.filter((methodKey) =>
      METHOD_OPTIONS.some((option) => option.key === methodKey),
    );
    const firstImplementedMethod = normalizedSelectedMethods.find(
      (methodKey) => METHOD_OPTIONS.find((option) => option.key === methodKey)?.isImplemented,
    );
    const effectiveMethod = firstImplementedMethod || normalizedSelectedMethods[0] || 'bishop_simplified';
    const searchSource = mode === 'single' ? DEF_SEARCH : search;
    const payload = {
      name: name.trim(),
      method: effectiveMethod,
      methods: normalizedSelectedMethods.length > 0 ? normalizedSelectedMethods : [effectiveMethod],
      sliceCount: intNum(sliceCount),
      groundProfile: profile.map((p) => ({ x: num(p.x), y: num(p.y) })),
      material: {
        name: material.name.trim(),
        cPrime: num(material.cPrime),
        phiPrime: num(material.phiPrime),
        gammaDry: num(material.gammaDry),
        gammaSat: num(material.gammaSat),
      },
      waterTable: { mode: 'horizontal', elevation: num(water.elevation), gammaWater: num(water.gammaWater) },
      search: {
        centerX: {
          min: num(searchSource.centerX.min),
          max: num(searchSource.centerX.max),
          count: intNum(searchSource.centerX.count),
        },
        centerY: {
          min: num(searchSource.centerY.min),
          max: num(searchSource.centerY.max),
          count: intNum(searchSource.centerY.count),
        },
        radius: {
          min: num(searchSource.radius.min),
          max: num(searchSource.radius.max),
          count: intNum(searchSource.radius.count),
        },
      },
      // Surface filters provide explicit geometric acceptance constraints for candidate circles.
      // When input fields are left blank, fallbacks are resolved from current ground profile bounds.
      surfaceFilters: {
        minArcElevation: Number.NaN,
        leftLimitX: Number.NaN,
        rightLimitX: Number.NaN,
        failureDirection: normalizeFailureDirection(surfaceFilters.failureDirection),
        allowReverseCurvature: Boolean(surfaceFilters.allowReverseCurvature),
      },
      analysisOptions: {
        tolerance: num(opts.tolerance),
        maxIterations: intNum(opts.maxIterations),
        maxReportedCandidates: intNum(opts.maxReportedCandidates),
        minValidMAlpha: num(opts.minValidMAlpha),
        bishopClosureModel: DEF_OPTS.bishopClosureModel,
        debugSlices: Boolean(opts.debugSlices),
      },
    };

    const profileBounds = computeProfileBounds(payload.groundProfile);
    const defaultLeftLimitX = Number.isFinite(profileBounds?.minX) ? profileBounds.minX : 0;
    const defaultRightLimitX = Number.isFinite(profileBounds?.maxX) ? profileBounds.maxX : 1;
    const defaultMinArcElevation = Number.isFinite(profileBounds?.minY) ? profileBounds.minY : 0;

    const minArcElevationInput = num(surfaceFilters.minArcElevation);
    const leftLimitXInput = num(surfaceFilters.leftLimitX);
    const rightLimitXInput = num(surfaceFilters.rightLimitX);

    payload.surfaceFilters = {
      minArcElevation: Number.isFinite(minArcElevationInput) ? minArcElevationInput : defaultMinArcElevation,
      leftLimitX: Number.isFinite(leftLimitXInput) ? leftLimitXInput : defaultLeftLimitX,
      rightLimitX: Number.isFinite(rightLimitXInput) ? rightLimitXInput : defaultRightLimitX,
      failureDirection: normalizeFailureDirection(surfaceFilters.failureDirection),
      allowReverseCurvature: Boolean(surfaceFilters.allowReverseCurvature),
    };

    if (mode === 'single')
      payload.singleSurface = { centerX: num(single.centerX), centerY: num(single.centerY), radius: num(single.radius) };
    return payload;
  }, [material, mode, name, opts, profile, search, selectedMethods, single, sliceCount, surfaceFilters, water]);

  const profileBounds = useMemo(() => computeProfileBounds(profile), [profile]);
  const minArcElevationValue = num(surfaceFilters.minArcElevation);
  const isMinArcElevationAuto = !Number.isFinite(minArcElevationValue);

  const validationErrors = useMemo(() => {
    const errs = [];
    if (!calculationInput.name) errs.push('Name is required.');
    if (calculationInput.name.length > MAX_NAME_LENGTH) errs.push(`Name cannot exceed ${MAX_NAME_LENGTH} characters.`);
    if (!Array.isArray(selectedMethods) || selectedMethods.length < 1) errs.push('Select at least one stability method.');
    const containsImplementedMethod = selectedMethods.some(
      (methodKey) => METHOD_OPTIONS.find((option) => option.key === methodKey)?.isImplemented,
    );
    if (!containsImplementedMethod) errs.push('At least one implemented method must be selected (Bishop Simplified).');
    const unknownMethods = selectedMethods.filter((methodKey) => !METHOD_OPTIONS.some((option) => option.key === methodKey));
    if (unknownMethods.length > 0) errs.push(`Unknown method selection: ${unknownMethods.join(', ')}.`);
    if (
      !Number.isInteger(calculationInput.sliceCount) ||
      calculationInput.sliceCount < MIN_SLICE_COUNT ||
      calculationInput.sliceCount > MAX_SLICE_COUNT
    ) {
      errs.push(`Slice count must be an integer between ${MIN_SLICE_COUNT} and ${MAX_SLICE_COUNT}.`);
    }
    if (calculationInput.groundProfile.length < MIN_GROUND_PROFILE_POINTS) {
      errs.push(`Ground profile must have at least ${MIN_GROUND_PROFILE_POINTS} points.`);
    }
    if (calculationInput.groundProfile.length > MAX_GROUND_PROFILE_POINTS) {
      errs.push(`Ground profile cannot exceed ${MAX_GROUND_PROFILE_POINTS} points.`);
    }
    for (let k = 0; k < calculationInput.groundProfile.length; k += 1) {
      const p = calculationInput.groundProfile[k];
      if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) errs.push('Ground profile points must be numeric.');
      if (k > 0 && p.x <= calculationInput.groundProfile[k - 1].x)
        errs.push('Ground profile x values must be strictly increasing.');
    }

    if (!calculationInput.material.name) errs.push('Material name is required.');
    if (calculationInput.material.name.length > MAX_MATERIAL_NAME_LENGTH)
      errs.push(`Material name cannot exceed ${MAX_MATERIAL_NAME_LENGTH} characters.`);
    if (!Number.isFinite(calculationInput.material.cPrime) || calculationInput.material.cPrime < 0) errs.push("c' must be >= 0.");
    if (
      !Number.isFinite(calculationInput.material.phiPrime) ||
      calculationInput.material.phiPrime < 0 ||
      calculationInput.material.phiPrime >= 90
    ) {
      errs.push("phi' must be between 0 and 90 degrees.");
    }
    if (!Number.isFinite(calculationInput.material.gammaDry) || calculationInput.material.gammaDry <= 0)
      errs.push('gammaDry must be > 0.');
    if (!Number.isFinite(calculationInput.material.gammaSat) || calculationInput.material.gammaSat <= 0)
      errs.push('gammaSat must be > 0.');

    if (!Number.isFinite(calculationInput.waterTable.elevation)) errs.push('Water table elevation must be numeric.');
    if (!Number.isFinite(calculationInput.waterTable.gammaWater) || calculationInput.waterTable.gammaWater <= 0)
      errs.push('gammaWater must be > 0.');

    if (mode === 'search') {
      ['centerX', 'centerY', 'radius'].forEach((axis) => {
        const axisCfg = calculationInput.search[axis];
        if (!Number.isFinite(axisCfg.min) || !Number.isFinite(axisCfg.max)) errs.push(`Search ${axis} min/max must be numeric.`);
        if (!Number.isInteger(axisCfg.count) || axisCfg.count < SEARCH_COUNT_MIN || axisCfg.count > SEARCH_COUNT_MAX) {
          errs.push(`Search ${axis} count must be an integer between ${SEARCH_COUNT_MIN} and ${SEARCH_COUNT_MAX}.`);
        }
        if (Number.isFinite(axisCfg.min) && Number.isFinite(axisCfg.max) && axisCfg.max <= axisCfg.min) {
          errs.push(`Search ${axis} max must be greater than min.`);
        }

        if (axis === 'radius') {
          if (Number.isFinite(axisCfg.min) && (axisCfg.min <= 0 || axisCfg.min > SEARCH_RADIUS_MAX)) {
            errs.push(`Search radius min must be > 0 and <= ${SEARCH_RADIUS_MAX}.`);
          }
          if (Number.isFinite(axisCfg.max) && (axisCfg.max <= 0 || axisCfg.max > SEARCH_RADIUS_MAX)) {
            errs.push(`Search radius max must be > 0 and <= ${SEARCH_RADIUS_MAX}.`);
          }
        } else {
          if (Number.isFinite(axisCfg.min) && Math.abs(axisCfg.min) > SEARCH_CENTER_ABS_MAX) {
            errs.push(`Search ${axis} min must be within ±${SEARCH_CENTER_ABS_MAX}.`);
          }
          if (Number.isFinite(axisCfg.max) && Math.abs(axisCfg.max) > SEARCH_CENTER_ABS_MAX) {
            errs.push(`Search ${axis} max must be within ±${SEARCH_CENTER_ABS_MAX}.`);
          }
        }
      });

      const candidateEstimate =
        calculationInput.search.centerX.count * calculationInput.search.centerY.count * calculationInput.search.radius.count;
      if (Number.isInteger(candidateEstimate) && candidateEstimate > SEARCH_TOTAL_CANDIDATES_MAX) {
        errs.push(`Search candidate count (${candidateEstimate}) cannot exceed ${SEARCH_TOTAL_CANDIDATES_MAX}.`);
      }
    }

    if (mode === 'single') {
      if (
        !Number.isFinite(calculationInput.singleSurface?.centerX) ||
        !Number.isFinite(calculationInput.singleSurface?.centerY)
      ) {
        errs.push('Single surface center coordinates must be numeric.');
      }
      if (
        Number.isFinite(calculationInput.singleSurface?.centerX) &&
        Math.abs(calculationInput.singleSurface.centerX) > SEARCH_CENTER_ABS_MAX
      ) {
        errs.push(`Single surface centerX must be within ±${SEARCH_CENTER_ABS_MAX}.`);
      }
      if (
        Number.isFinite(calculationInput.singleSurface?.centerY) &&
        Math.abs(calculationInput.singleSurface.centerY) > SEARCH_CENTER_ABS_MAX
      ) {
        errs.push(`Single surface centerY must be within ±${SEARCH_CENTER_ABS_MAX}.`);
      }
      if (
        !Number.isFinite(calculationInput.singleSurface?.radius) ||
        calculationInput.singleSurface.radius <= 0 ||
        calculationInput.singleSurface.radius > SEARCH_RADIUS_MAX
      ) {
        errs.push(`Single surface radius must be > 0 and <= ${SEARCH_RADIUS_MAX}.`);
      }
    }

    // Surface filter guards keep acceptance constraints predictable and bounded.
    if (
      !Number.isFinite(calculationInput.surfaceFilters?.minArcElevation) ||
      Math.abs(calculationInput.surfaceFilters.minArcElevation) > SEARCH_CENTER_ABS_MAX
    ) {
      errs.push(`Min arc elevation must be finite and within ±${SEARCH_CENTER_ABS_MAX}.`);
    }

    if (
      !Number.isFinite(calculationInput.surfaceFilters?.leftLimitX) ||
      !Number.isFinite(calculationInput.surfaceFilters?.rightLimitX)
    ) {
      errs.push('Left/Right slope limits must be numeric.');
    } else if (calculationInput.surfaceFilters.rightLimitX <= calculationInput.surfaceFilters.leftLimitX) {
      errs.push('Right slope limit must be greater than left slope limit.');
    }

    const finiteProfilePoints = calculationInput.groundProfile.filter(
      (point) => Number.isFinite(point.x) && Number.isFinite(point.y),
    );
    if (finiteProfilePoints.length >= 2) {
      const profileMinX = Math.min(...finiteProfilePoints.map((point) => point.x));
      const profileMaxX = Math.max(...finiteProfilePoints.map((point) => point.x));
      if (
        Number.isFinite(calculationInput.surfaceFilters?.leftLimitX) &&
        (calculationInput.surfaceFilters.leftLimitX < profileMinX || calculationInput.surfaceFilters.leftLimitX > profileMaxX)
      ) {
        errs.push(`Left slope limit must stay within ground profile x-range [${profileMinX}, ${profileMaxX}].`);
      }
      if (
        Number.isFinite(calculationInput.surfaceFilters?.rightLimitX) &&
        (calculationInput.surfaceFilters.rightLimitX < profileMinX || calculationInput.surfaceFilters.rightLimitX > profileMaxX)
      ) {
        errs.push(`Right slope limit must stay within ground profile x-range [${profileMinX}, ${profileMaxX}].`);
      }
    }
    if (!FAILURE_DIRECTION_OPTIONS.some((option) => option.key === calculationInput.surfaceFilters?.failureDirection)) {
      errs.push('Failure direction must be Auto, Left to Right, or Right to Left.');
    }

    if (
      !Number.isFinite(calculationInput.analysisOptions.tolerance) ||
      calculationInput.analysisOptions.tolerance < OPTIONS_TOLERANCE_MIN ||
      calculationInput.analysisOptions.tolerance > OPTIONS_TOLERANCE_MAX
    ) {
      errs.push(`Tolerance must be between ${OPTIONS_TOLERANCE_MIN} and ${OPTIONS_TOLERANCE_MAX}.`);
    }
    if (
      !Number.isInteger(calculationInput.analysisOptions.maxIterations) ||
      calculationInput.analysisOptions.maxIterations < OPTIONS_MAX_ITERATIONS_MIN ||
      calculationInput.analysisOptions.maxIterations > OPTIONS_MAX_ITERATIONS_MAX
    ) {
      errs.push(`Max iterations must be an integer between ${OPTIONS_MAX_ITERATIONS_MIN} and ${OPTIONS_MAX_ITERATIONS_MAX}.`);
    }
    if (
      !Number.isInteger(calculationInput.analysisOptions.maxReportedCandidates) ||
      calculationInput.analysisOptions.maxReportedCandidates < OPTIONS_MAX_REPORTED_CANDIDATES_MIN ||
      calculationInput.analysisOptions.maxReportedCandidates > OPTIONS_MAX_REPORTED_CANDIDATES_MAX
    ) {
      errs.push(
        `Max reported candidates must be an integer between ${OPTIONS_MAX_REPORTED_CANDIDATES_MIN} and ${OPTIONS_MAX_REPORTED_CANDIDATES_MAX}.`,
      );
    }
    if (
      !Number.isFinite(calculationInput.analysisOptions.minValidMAlpha) ||
      calculationInput.analysisOptions.minValidMAlpha < OPTIONS_MIN_VALID_M_ALPHA_MIN ||
      calculationInput.analysisOptions.minValidMAlpha > OPTIONS_MIN_VALID_M_ALPHA_MAX
    ) {
      errs.push(`minValidMAlpha must be between ${OPTIONS_MIN_VALID_M_ALPHA_MIN} and ${OPTIONS_MIN_VALID_M_ALPHA_MAX}.`);
    }
    if (!['n_force_calibrated', 'classic_wu_over_m'].includes(calculationInput.analysisOptions.bishopClosureModel)) {
      errs.push('Closure model must be n_force_calibrated or classic_wu_over_m.');
    }
    return errs;
  }, [MAX_NAME_LENGTH, calculationInput, mode, selectedMethods]);

  useEffect(() => {
    const invalid = validationErrors.length > 0;
    setCalculateButtonDisabled(invalid);
    setSaveButtonDisabled(invalid);
  }, [setCalculateButtonDisabled, setSaveButtonDisabled, validationErrors]);

  useEffect(() => {
    setShowOutput(false);
  }, [setShowOutput, sliceCount, profile, material, water, mode, single, search, surfaceFilters, opts, selectedMethods]);

  const createNewCalculation = () => {
    setName('Case-01');
    setSliceCount('24');
    setMode('search');
    setProfile(DEF_PROFILE);
    setMaterial(DEF_MATERIAL);
    setWater(DEF_WATER);
    setSingle(DEF_SINGLE);
    setSearch(DEF_SEARCH);
    setSurfaceFilters({ ...DEF_SURFACE_FILTERS });
    setOpts(DEF_OPTS);
    setSelectedMethods(['bishop_simplified']);
    setHeatmapPalette('default');
    setShowOutput(false);
  };

  const handleInputTabChange = (_, newValue) => {
    setInputTabValue(newValue);
  };

  const output = result?.output || {};
  const critical = output?.criticalSurface || null;
  const searchHeatmap = output?.searchHeatmap || null;
  const diagnostics = output?.diagnostics || {};
  const resultDebugSlicesEnabled = Boolean(result?.input?.analysisOptions?.debugSlices);
  const criticalSliceDebug = Array.isArray(critical?.sliceDebug) ? critical.sliceDebug : [];
  const shouldShowSliceDebugSection = Boolean(output?.singleSurfaceMode && resultDebugSlicesEnabled);
  const minSliceDebugMAlpha = criticalSliceDebug.reduce((minValue, row) => {
    const current = Number(row?.mAlpha);
    if (!Number.isFinite(current)) return minValue;
    return current < minValue ? current : minValue;
  }, Number.POSITIVE_INFINITY);
  const activeSurfaceFilters = diagnostics?.surfaceFilters || result?.input?.surfaceFilters || null;
  const activeLeftLimitX = activeSurfaceFilters?.leftLimitX ?? activeSurfaceFilters?.entryX?.min;
  const activeRightLimitX = activeSurfaceFilters?.rightLimitX ?? activeSurfaceFilters?.exitX?.max;
  const activeFailureDirection = normalizeFailureDirection(activeSurfaceFilters?.failureDirection);
  const summaryCriticalCards = [
    { label: 'FS', value: fixed(critical?.factorOfSafety, 6) },
    { label: 'Converged', value: critical ? (critical?.converged ? 'Yes' : 'No') : '-' },
    { label: 'Iterations', value: critical?.iterationCount ?? '-' },
    { label: 'Closure', value: critical?.solver?.closureMode || '-' },
  ];
  const summaryDiagnosticsCards = [
    { label: 'Total Candidates', value: diagnostics?.totalCandidates ?? '-' },
    { label: 'Valid Candidates', value: diagnostics?.validCandidates ?? '-' },
    { label: 'Skipped Reverse Curvature', value: diagnostics?.skippedByReverseCurvature ?? 0 },
    { label: 'Skipped Slope Limits', value: diagnostics?.skippedBySlopeLimits ?? diagnostics?.skippedByEntryExitRange ?? 0 },
    { label: 'Skipped Failure Direction', value: diagnostics?.skippedByFailureDirection ?? 0 },
    { label: 'Skipped Min Arc Elevation', value: diagnostics?.skippedByMinArcElevation ?? 0 },
  ];
  const summarySurfaceFilterCards = activeSurfaceFilters
    ? [
        { label: 'Min Arc Elevation', value: `${fixed(activeSurfaceFilters?.minArcElevation, 3)} m` },
        { label: 'Left Limit X', value: fixed(activeLeftLimitX, 3) },
        { label: 'Right Limit X', value: fixed(activeRightLimitX, 3) },
        { label: 'Failure Direction', value: getFailureDirectionLabel(activeFailureDirection) },
        { label: 'Reverse Curvature', value: Boolean(activeSurfaceFilters?.allowReverseCurvature) ? 'Allowed' : 'Blocked' },
      ]
    : [];
  const resultTone = useMemo(
    () =>
      appTheme === 'dark'
        ? {
            paperBg: 'background.paper',
            headerGradient: `linear-gradient(90deg, ${alpha('#42a5f5', 0.18)}, ${alpha('#66bb6a', 0.18)})`,
            fsBadgeBg: alpha('#66bb6a', 0.18),
            fsBadgeBorder: alpha('#81c784', 0.46),
            fsBadgeText: '#b9f6ca',
            cardBorder: alpha('#b0bec5', 0.22),
            cardShadow: '0 8px 20px rgba(0, 0, 0, 0.18)',
            criticalCardBg: alpha('#ffffff', 0.03),
            diagnosticsCardBg: alpha('#64b5f6', 0.1),
            filterCardBg: alpha('#26c6da', 0.12),
            filterCardBorder: alpha('#4dd0e1', 0.28),
            tableBg: alpha('#0f151b', 0.58),
            tableBorder: alpha('#b0bec5', 0.2),
            tableHeaderBg: '#1a2430',
            tableHeaderText: '#d7e1e8',
            tableRowOddBg: alpha('#ffffff', 0.02),
            tableRowEvenBg: alpha('#90caf9', 0.05),
            tableHoverBg: alpha('#90caf9', 0.08),
          }
        : {
            paperBg: 'background.paper',
            headerGradient: 'linear-gradient(90deg, rgba(25,118,210,0.08), rgba(46,125,50,0.08))',
            fsBadgeBg: 'rgba(46,125,50,0.14)',
            fsBadgeBorder: 'rgba(46,125,50,0.35)',
            fsBadgeText: 'success.dark',
            cardBorder: 'divider',
            cardShadow: 'none',
            criticalCardBg: 'background.paper',
            diagnosticsCardBg: '#fafafa',
            filterCardBg: 'rgba(2,136,209,0.05)',
            filterCardBorder: 'divider',
            tableBg: 'background.paper',
            tableBorder: 'divider',
            tableHeaderBg: '#f5f7fa',
            tableHeaderText: '#455a64',
            tableRowOddBg: '#ffffff',
            tableRowEvenBg: '#fafbfc',
            tableHoverBg: 'rgba(25,118,210,0.05)',
          },
    [appTheme],
  );
  const summaryCardBaseSx = {
    border: '1px solid',
    borderColor: resultTone.cardBorder,
    borderRadius: 1.2,
    px: 1.2,
    py: 0.9,
    boxShadow: resultTone.cardShadow,
  };
  const sliceDiagnosticsTableSx = {
    mt: 1,
    maxHeight: 320,
    borderColor: resultTone.tableBorder,
    bgcolor: resultTone.tableBg,
    '& .MuiTableCell-root': {
      borderBottomColor: resultTone.tableBorder,
    },
    '& .MuiTableCell-stickyHeader': {
      bgcolor: resultTone.tableHeaderBg,
      color: resultTone.tableHeaderText,
      backgroundImage: 'none',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      bgcolor: resultTone.tableHeaderBg,
      color: resultTone.tableHeaderText,
      fontWeight: 700,
      whiteSpace: 'nowrap',
    },
    '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd) .MuiTableCell-root': {
      bgcolor: resultTone.tableRowOddBg,
    },
    '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even) .MuiTableCell-root': {
      bgcolor: resultTone.tableRowEvenBg,
    },
    '& .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-root': {
      bgcolor: resultTone.tableHoverBg,
    },
  };

  return (
    <>
      <SeoHelmet
        title="GeoCalc | Slope Stability Calculator"
        description="Analyze circular slip surfaces with Bishop Simplified method using single-surface and grid-search modes, groundwater effects, and factor-of-safety visualization."
        url="https://www.geocalc.app/calculations/slope-stability"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="tool"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'Slope Stability', url: 'https://www.geocalc.app/calculations/slope-stability' },
        ]}
      />
      <Header />
      <div className="container">
        <MuiSnackbar open={open} setOpen={setOpen} severity={severity} alertMessage={alertMessage} />
        {displaySidebar ? (
          <Sidebar
            URL={URL}
            loadAllCalculations={loadAllCalculations}
            calculations={calculations}
            loadOneCalculation={loadOneCalculation}
            calculate={calculate}
            setInputs={setInputs}
            deleteCalculations={deleteCalculations}
            createNewCalculation={createNewCalculation}
            isLoading={isLoadingLoadAll}
            handleSidebarDisplay={handleSidebarDisplay}
            displaySidebar={displaySidebar}
            setDisplaySidebar={setDisplaySidebar}
          />
        ) : (
          <MenuOpenIcon color="primary" fontSize="large" className="sidebar-open-icon" onClick={handleSidebarDisplay} />
        )}
        <DecisionModal
          URL={URL}
          data={calculationInput}
          decisionModalIsOpen={decisionModalIsOpen}
          setDecisionModalIsOpenToFalse={setDecisionModalIsOpenToFalse}
          decision={decision}
          setDecision={setDecision}
          checkCalculationNameforSavingAs={checkCalculationNameforSavingAs}
          name={name}
          setName={setName}
          errorMessage={errorMessage}
          update={update}
          saveButtonDisabled={saveButtonDisabled}
        />
        <div className="right-calculation">
          <h1>Slope Stability</h1>
          <Stack spacing={2} sx={{ p: 1 }}>
            {validationErrors.length > 0 && <Alert severity="warning">{validationErrors[0]}</Alert>}
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="stretch">
              <Paper
                variant="outlined"
                sx={{
                  width: { xs: '100%', lg: 500 },
                  flex: { xs: '1 1 auto', lg: '0 0 500px' },
                  minWidth: 0,
                }}
              >
                <Tabs
                  value={inputTabValue}
                  onChange={handleInputTabChange}
                  variant="scrollable"
                  scrollButtons
                  allowScrollButtonsMobile
                  sx={{ px: 1, pt: 1, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  <Tab label="General" sx={{ textTransform: 'none' }} />
                  <Tab label="Ground Profile" sx={{ textTransform: 'none' }} />
                  <Tab label="Material" sx={{ textTransform: 'none' }} />
                  <Tab label="Ground Water" sx={{ textTransform: 'none' }} />
                  <Tab label="Mode & Search" sx={{ textTransform: 'none' }} />
                  <Tab label="Options" sx={{ textTransform: 'none' }} />
                </Tabs>
                <Stack spacing={2} sx={{ p: 2 }}>
                  {inputTabValue === 0 && (
                    <>
                      <Typography variant="h6">General</Typography>
                      <Stack spacing={2}>
                        <TextField
                          fullWidth
                          size="small"
                          label={labelWithHelp(
                            'Name',
                            `Case name shown in saved calculations. Keep it short and descriptive (max ${MAX_NAME_LENGTH} chars).`,
                          )}
                          value={name}
                          onChange={(e) => {
                            if (e.target.value.length > MAX_NAME_LENGTH) {
                              showMuiSnack(`Calculation name can be max ${MAX_NAME_LENGTH} characters.`, 'warning');
                            }
                            setName(e.target.value);
                          }}
                          inputProps={{ maxLength: MAX_NAME_LENGTH }}
                          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                        />
                        <FormControl size="small" fullWidth>
                          <InputLabel sx={{ pointerEvents: 'auto' }}>
                            {labelWithHelp(
                              'Methods',
                              'Choose one or more methods. The current run uses the first implemented method in your selection.',
                            )}
                          </InputLabel>
                          <Select
                            multiple
                            label="Methods"
                            value={selectedMethods}
                            onChange={(e) => {
                              const value = e.target.value;
                              setSelectedMethods(Array.isArray(value) ? value : [String(value)]);
                            }}
                            renderValue={(selected) => selected.map((methodKey) => getMethodLabel(methodKey)).join(', ')}
                            disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                          >
                            {METHOD_OPTIONS.map((option) => (
                              <MenuItem key={option.key} value={option.key}>
                                <Checkbox checked={selectedMethods.indexOf(option.key) > -1} />
                                <ListItemText
                                  primary={option.label}
                                  secondary={option.isImplemented ? undefined : 'Not implemented yet'}
                                  secondaryTypographyProps={{ sx: { color: 'warning.main' } }}
                                />
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{`Current run uses first implemented method: ${getMethodLabel(calculationInput.method)}`}</FormHelperText>
                        </FormControl>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={opts.debugSlices}
                              onChange={(e) => setOpts((prev) => ({ ...prev, debugSlices: e.target.checked }))}
                              disabled={mode !== 'single' || isLoadingCalculate || isLoadingLoad || isLoadingSave}
                            />
                          }
                          label={labelWithHelp(
                            'Debug Slices',
                            'Single-surface only. When enabled, per-slice diagnostics are returned for the critical surface.',
                          )}
                        />
                        <FormControl size="small" fullWidth>
                          <InputLabel sx={{ pointerEvents: 'auto' }}>
                            {labelWithHelp(
                              'Heatmap Palette',
                              'Color scale used for grid-search FS heatmap visualization only. Does not affect calculations.',
                            )}
                          </InputLabel>
                          <Select
                            label="Heatmap Palette"
                            value={normalizeHeatmapPalette(heatmapPalette)}
                            onChange={(e) => setHeatmapPalette(normalizeHeatmapPalette(e.target.value))}
                            disabled={mode !== 'search' || isLoadingCalculate || isLoadingLoad || isLoadingSave}
                          >
                            {HEATMAP_PALETTE_OPTIONS.map((option) => (
                              <MenuItem key={option.key} value={option.key}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            {mode === 'search'
                              ? 'Default uses the current scheme; colorblind-safe uses a viridis-like scale.'
                              : 'Heatmap palette is used only in Grid Search mode.'}
                          </FormHelperText>
                        </FormControl>
                      </Stack>
                    </>
                  )}

                  {inputTabValue === 1 && (
                    <>
                      <Typography variant="h6">Ground Profile Coordinates</Typography>
                      {profile.map((pt, idx) => (
                        <Stack
                          key={`pt-${idx + 1}`}
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          sx={{ flexWrap: 'wrap' }}
                        >
                          <TextField
                            fullWidth
                            size="small"
                          label={labelWithHelp(
                            `P${idx + 1} x`,
                            'Horizontal coordinate (m). X values must increase from left to right.',
                          )}
                          type="number"
                          inputProps={mergeNumberInputProps()}
                          value={pt.x}
                          onChange={(e) =>
                            setProfile((prev) => prev.map((p, i) => (i === idx ? { ...p, x: e.target.value } : p)))
                            }
                            disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                            sx={{ flex: '1 1 130px' }}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            label={labelWithHelp(`P${idx + 1} y`, 'Vertical coordinate (m).')}
                            type="number"
                            inputProps={mergeNumberInputProps()}
                            value={pt.y}
                            onChange={(e) =>
                              setProfile((prev) => prev.map((p, i) => (i === idx ? { ...p, y: e.target.value } : p)))
                            }
                            disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                            sx={{ flex: '1 1 130px' }}
                          />
                          <Tooltip title={`Remove P${idx + 1}`}>
                            <span>
                              <IconButton
                                aria-label={`remove ground profile point ${idx + 1}`}
                                color="error"
                                disabled={
                                  profile.length <= MIN_GROUND_PROFILE_POINTS ||
                                  isLoadingCalculate ||
                                  isLoadingLoad ||
                                  isLoadingSave
                                }
                                onClick={() => setProfile((prev) => prev.filter((_, i) => i !== idx))}
                                sx={{
                                  alignSelf: { xs: 'stretch', sm: 'flex-start' },
                                  border: '1px solid',
                                  borderColor: 'error.main',
                                  borderRadius: 1,
                                }}
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      ))}
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setProfile((prev) => [...prev, { x: '', y: '' }])}
                        disabled={
                          profile.length >= MAX_GROUND_PROFILE_POINTS || isLoadingCalculate || isLoadingLoad || isLoadingSave
                        }
                      >
                        Add Point
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        {`Points: ${profile.length} / ${MAX_GROUND_PROFILE_POINTS}`}
                      </Typography>
                    </>
                  )}

                  {inputTabValue === 2 && (
                    <>
                      <Typography variant="h6">Material</Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                          gap: 2,
                        }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          label={labelWithHelp(
                            'Material Name',
                            `Label for this material set (max ${MAX_MATERIAL_NAME_LENGTH} chars).`,
                          )}
                          value={material.name}
                          onChange={(e) => setMaterial((prev) => ({ ...prev, name: e.target.value }))}
                          inputProps={{ maxLength: MAX_MATERIAL_NAME_LENGTH }}
                          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label={labelWithHelp(
                            "c' (kPa)",
                            "Effective cohesion c' in drained strength calculations. Must be >= 0.",
                          )}
                          type="number"
                          inputProps={mergeNumberInputProps()}
                          value={material.cPrime}
                          onChange={(e) => setMaterial((prev) => ({ ...prev, cPrime: e.target.value }))}
                          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label={labelWithHelp('φ′ (deg)', "Effective friction angle phi' in degrees. Valid range: 0 to < 90.")}
                          type="number"
                          inputProps={mergeNumberInputProps()}
                          value={material.phiPrime}
                          onChange={(e) => setMaterial((prev) => ({ ...prev, phiPrime: e.target.value }))}
                          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label={labelWithHelp('γdry (kN/m³)', 'Dry unit weight of soil. Must be > 0.')}
                          type="number"
                          inputProps={mergeNumberInputProps()}
                          value={material.gammaDry}
                          onChange={(e) => setMaterial((prev) => ({ ...prev, gammaDry: e.target.value }))}
                          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label={labelWithHelp('γsat (kN/m³)', 'Saturated unit weight of soil. Must be > 0.')}
                          type="number"
                          inputProps={mergeNumberInputProps()}
                          value={material.gammaSat}
                          onChange={(e) => setMaterial((prev) => ({ ...prev, gammaSat: e.target.value }))}
                          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                        />
                      </Box>
                    </>
                  )}

                  {inputTabValue === 3 && (
                    <>
                      <Typography variant="h6">Ground Water</Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                          gap: 2,
                        }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          label={labelWithHelp('Water Elevation (m)', 'Horizontal water-table elevation (y-coordinate).')}
                          type="number"
                          inputProps={mergeNumberInputProps()}
                          value={water.elevation}
                          onChange={(e) => setWater((prev) => ({ ...prev, elevation: e.target.value }))}
                          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label={labelWithHelp(
                            'γw (kN/m³)',
                            'Unit weight of water for pore-pressure calculations (typically 9.81).',
                          )}
                          type="number"
                          inputProps={mergeNumberInputProps()}
                          value={water.gammaWater}
                          onChange={(e) => setWater((prev) => ({ ...prev, gammaWater: e.target.value }))}
                          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                        />
                      </Box>
                    </>
                  )}

                  {inputTabValue === 4 && (
                    <>
                      <Typography variant="subtitle2" sx={{ pt: 0.25, fontWeight: 700, color: 'text.secondary' }}>
                        Analysis Mode
                      </Typography>
                      <RadioGroup
                        row
                        value={mode}
                        onChange={(e) => setMode(e.target.value)}
                        sx={{ justifyContent: 'center', width: '100%' }}
                      >
                        <FormControlLabel
                          value="single"
                          control={<Radio />}
                          label={labelWithHelp('Single Surface', 'Analyze one user-defined slip circle (center + radius).')}
                        />
                        <FormControlLabel
                          value="search"
                          control={<Radio />}
                          label={labelWithHelp(
                            'Grid Search',
                            'Evaluate many candidate circles over ranges and return the lowest FS.',
                          )}
                        />
                      </RadioGroup>
                      {mode === 'single' && (
                        <>
                          <Typography variant="subtitle2" sx={{ pt: 0.5, fontWeight: 700, color: 'text.secondary' }}>
                            Single Surface Geometry
                          </Typography>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                              gap: 2,
                            }}
                          >
                            <TextField
                              fullWidth
                              size="small"
                              label={labelWithHelp(
                                'Center X',
                                `Slip-circle center X (m). Allowed range: ±${SEARCH_CENTER_ABS_MAX}.`,
                              )}
                              type="number"
                              value={single.centerX}
                              onChange={(e) => setSingle((prev) => ({ ...prev, centerX: e.target.value }))}
                              inputProps={mergeNumberInputProps({ min: -SEARCH_CENTER_ABS_MAX, max: SEARCH_CENTER_ABS_MAX })}
                              disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                            />
                            <TextField
                              fullWidth
                              size="small"
                              label={labelWithHelp(
                                'Center Y',
                                `Slip-circle center Y (m). Allowed range: ±${SEARCH_CENTER_ABS_MAX}.`,
                              )}
                              type="number"
                              value={single.centerY}
                              onChange={(e) => setSingle((prev) => ({ ...prev, centerY: e.target.value }))}
                              inputProps={mergeNumberInputProps({ min: -SEARCH_CENTER_ABS_MAX, max: SEARCH_CENTER_ABS_MAX })}
                              disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                            />
                            <TextField
                              fullWidth
                              size="small"
                              label={labelWithHelp('Radius', `Slip-circle radius (m). Must be > 0 and <= ${SEARCH_RADIUS_MAX}.`)}
                              type="number"
                              value={single.radius}
                              onChange={(e) => setSingle((prev) => ({ ...prev, radius: e.target.value }))}
                              inputProps={mergeNumberInputProps({ min: 0.001, max: SEARCH_RADIUS_MAX })}
                              disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                            />
                          </Box>
                        </>
                      )}
                      {mode === 'single' && (
                        <Typography variant="caption" color="text.secondary">
                          Search bounds are not used in single-surface mode.
                        </Typography>
                      )}
                      {mode === 'search' && (
                        <>
                          <Typography variant="subtitle2" sx={{ pt: 0.5, fontWeight: 700, color: 'text.secondary' }}>
                            Grid Search Ranges
                          </Typography>
                          {['centerX', 'centerY', 'radius'].map((axis) => (
                            <Box
                              key={axis}
                              sx={{
                                display: 'grid',
                                gap: 0.75,
                              }}
                            >
                              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                {axis}
                              </Typography>
                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                                  gap: 1,
                                }}
                              >
                                <TextField
                                  fullWidth
                                  size="small"
                                  label={labelWithHelp(
                                    'min',
                                    axis === 'radius'
                                      ? `Minimum radius value. Must be > 0 and <= ${SEARCH_RADIUS_MAX}.`
                                      : `Minimum value for ${axis}. Allowed range: ±${SEARCH_CENTER_ABS_MAX}.`,
                                  )}
                                  type="number"
                                  value={search[axis].min}
                                  onChange={(e) =>
                                    setSearch((prev) => ({ ...prev, [axis]: { ...prev[axis], min: e.target.value } }))
                                  }
                                  inputProps={mergeNumberInputProps(
                                    axis === 'radius'
                                      ? { min: 0.001, max: SEARCH_RADIUS_MAX }
                                      : { min: -SEARCH_CENTER_ABS_MAX, max: SEARCH_CENTER_ABS_MAX },
                                  )}
                                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                                  sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: 13 } }}
                                />
                                <TextField
                                  fullWidth
                                  size="small"
                                  label={labelWithHelp(
                                    'max',
                                    axis === 'radius'
                                      ? `Maximum radius value. Must be > 0, <= ${SEARCH_RADIUS_MAX}, and greater than min.`
                                      : `Maximum value for ${axis}. Allowed range: ±${SEARCH_CENTER_ABS_MAX}, and greater than min.`,
                                  )}
                                  type="number"
                                  value={search[axis].max}
                                  onChange={(e) =>
                                    setSearch((prev) => ({ ...prev, [axis]: { ...prev[axis], max: e.target.value } }))
                                  }
                                  inputProps={mergeNumberInputProps(
                                    axis === 'radius'
                                      ? { min: 0.001, max: SEARCH_RADIUS_MAX }
                                      : { min: -SEARCH_CENTER_ABS_MAX, max: SEARCH_CENTER_ABS_MAX },
                                  )}
                                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                                  sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: 13 } }}
                                />
                                <TextField
                                  fullWidth
                                  size="small"
                                  label={labelWithHelp(
                                    'count',
                                    `Number of grid samples on ${axis}. Integer range: ${SEARCH_COUNT_MIN} to ${SEARCH_COUNT_MAX}.`,
                                  )}
                                  type="number"
                                  value={search[axis].count}
                                  onChange={(e) =>
                                    setSearch((prev) => ({ ...prev, [axis]: { ...prev[axis], count: e.target.value } }))
                                  }
                                  inputProps={mergeNumberInputProps({ min: SEARCH_COUNT_MIN, max: SEARCH_COUNT_MAX, step: 1 })}
                                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                                  sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: 13 } }}
                                />
                              </Box>
                            </Box>
                          ))}
                        </>
                      )}
                      <Typography variant="subtitle2" sx={{ pt: 0.5, fontWeight: 700, color: 'text.secondary' }}>
                        Surface Filters
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Surface filters are applied in both single and grid-search modes. They control accepted circles before FS
                        ranking.
                      </Typography>
                      <Typography variant="subtitle2" sx={{ pt: 0.5, fontWeight: 700, color: 'text.secondary' }}>
                        Arc Elevation and Direction
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                          gap: 1,
                        }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          label={labelWithHelp(
                            'Min Arc Elevation (m, optional)',
                            'Minimum allowed elevation of slip arc. Leave empty for Auto (profile minimum Y).',
                          )}
                          type="number"
                          value={surfaceFilters.minArcElevation}
                          onChange={(e) => setSurfaceFilters((prev) => ({ ...prev, minArcElevation: e.target.value }))}
                          placeholder={Number.isFinite(profileBounds?.minY) ? String(profileBounds.minY) : ''}
                          inputProps={mergeNumberInputProps({ min: -SEARCH_CENTER_ABS_MAX, max: SEARCH_CENTER_ABS_MAX, step: 'any' })}
                          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                          helperText={
                            Number.isFinite(profileBounds?.minY)
                              ? isMinArcElevationAuto
                                ? `Auto from profile min Y: ${fixed(profileBounds.minY, 3)} m`
                                : `Manual value active. Clear input to use auto (${fixed(profileBounds.minY, 3)} m).`
                              : 'Enter a lower elevation limit, or leave empty to use profile minimum Y.'
                          }
                        />
                        <FormControl size="small" fullWidth>
                          <InputLabel sx={{ pointerEvents: 'auto' }}>
                            {labelWithHelp(
                              'Failure Direction',
                              'Direction filter for accepted surfaces. Auto keeps both; L->R or R->L restricts candidates.',
                            )}
                          </InputLabel>
                          <Select
                            label="Failure Direction"
                            value={normalizeFailureDirection(surfaceFilters.failureDirection)}
                            onChange={(e) =>
                              setSurfaceFilters((prev) => ({
                                ...prev,
                                failureDirection: normalizeFailureDirection(e.target.value),
                              }))
                            }
                            disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                          >
                            {FAILURE_DIRECTION_OPTIONS.map((option) => (
                              <MenuItem key={option.key} value={option.key}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>Auto keeps both directions; choose one to filter candidates.</FormHelperText>
                        </FormControl>
                      </Box>
                      <Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={surfaceFilters.allowReverseCurvature}
                              onChange={(e) =>
                                setSurfaceFilters((prev) => ({
                                  ...prev,
                                  allowReverseCurvature: e.target.checked,
                                }))
                              }
                              disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                            />
                          }
                          label={labelWithHelp(
                            'Allow Reverse Curvature',
                            'If OFF, circles centered below the slope crest are rejected.',
                          )}
                        />
                      </Box>
                      <Typography variant="subtitle2" sx={{ pt: 0.25, fontWeight: 700, color: 'text.secondary' }}>
                        Slope Limits
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                          gap: 1,
                        }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          label={labelWithHelp(
                            'Left Limit X',
                            'Left slope limit: slip entry point must be at or to the right of this X.',
                          )}
                          type="number"
                          value={surfaceFilters.leftLimitX}
                          onChange={(e) => setSurfaceFilters((prev) => ({ ...prev, leftLimitX: e.target.value }))}
                          placeholder={Number.isFinite(profileBounds?.minX) ? String(profileBounds.minX) : ''}
                          inputProps={mergeNumberInputProps({ step: 'any' })}
                          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label={labelWithHelp(
                            'Right Limit X',
                            'Right slope limit: slip exit point must be at or to the left of this X.',
                          )}
                          type="number"
                          value={surfaceFilters.rightLimitX}
                          onChange={(e) => setSurfaceFilters((prev) => ({ ...prev, rightLimitX: e.target.value }))}
                          placeholder={Number.isFinite(profileBounds?.maxX) ? String(profileBounds.maxX) : ''}
                          inputProps={mergeNumberInputProps({ step: 'any' })}
                          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {Number.isFinite(profileBounds?.minX) &&
                        Number.isFinite(profileBounds?.maxX) &&
                        Number.isFinite(profileBounds?.minY)
                          ? `Profile bounds: x [${fixed(profileBounds.minX, 3)}, ${fixed(profileBounds.maxX, 3)}], y(min) ${fixed(profileBounds.minY, 3)}.`
                          : 'Profile bounds are available after entering valid ground profile points.'}
                      </Typography>
                    </>
                  )}

                  {inputTabValue === 5 && (
                    <>
                      <Typography variant="h6">Analysis Options</Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                          gap: 2,
                        }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          label={labelWithHelp(
                            'Slice Count',
                            `Number of vertical slices. More slices improve resolution but increase runtime. Range: ${MIN_SLICE_COUNT}-${MAX_SLICE_COUNT}.`,
                          )}
                          type="number"
                          value={sliceCount}
                          onChange={(e) => setSliceCount(e.target.value)}
                          inputProps={mergeNumberInputProps({ min: MIN_SLICE_COUNT, max: MAX_SLICE_COUNT, step: 1 })}
                          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label={labelWithHelp(
                            'Tolerance',
                            `Convergence target for FS iteration. Lower values are stricter and may need more iterations. Range: ${OPTIONS_TOLERANCE_MIN}-${OPTIONS_TOLERANCE_MAX}.`,
                          )}
                          type="number"
                          value={opts.tolerance}
                          onChange={(e) => setOpts((prev) => ({ ...prev, tolerance: e.target.value }))}
                          inputProps={mergeNumberInputProps({ min: OPTIONS_TOLERANCE_MIN, max: OPTIONS_TOLERANCE_MAX, step: 'any' })}
                          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label={labelWithHelp(
                            'Max Iterations',
                            `Maximum solver iterations per candidate. Increase if convergence warnings appear. Range: ${OPTIONS_MAX_ITERATIONS_MIN}-${OPTIONS_MAX_ITERATIONS_MAX}.`,
                          )}
                          type="number"
                          value={opts.maxIterations}
                          onChange={(e) => setOpts((prev) => ({ ...prev, maxIterations: e.target.value }))}
                          inputProps={mergeNumberInputProps({ min: OPTIONS_MAX_ITERATIONS_MIN, max: OPTIONS_MAX_ITERATIONS_MAX, step: 1 })}
                          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label={labelWithHelp(
                            'Max Reported Candidates',
                            `How many best (lowest FS) valid surfaces are returned. Range: ${OPTIONS_MAX_REPORTED_CANDIDATES_MIN}-${OPTIONS_MAX_REPORTED_CANDIDATES_MAX}.`,
                          )}
                          type="number"
                          value={opts.maxReportedCandidates}
                          onChange={(e) => setOpts((prev) => ({ ...prev, maxReportedCandidates: e.target.value }))}
                          inputProps={mergeNumberInputProps({
                            min: OPTIONS_MAX_REPORTED_CANDIDATES_MIN,
                            max: OPTIONS_MAX_REPORTED_CANDIDATES_MAX,
                            step: 1,
                          })}
                          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label={labelWithHelp(
                            'Min Valid M-Alpha',
                            `Numerical stability threshold for Bishop iteration. Candidates below this are rejected. Range: ${OPTIONS_MIN_VALID_M_ALPHA_MIN}-${OPTIONS_MIN_VALID_M_ALPHA_MAX}.`,
                          )}
                          type="number"
                          value={opts.minValidMAlpha}
                          onChange={(e) => setOpts((prev) => ({ ...prev, minValidMAlpha: e.target.value }))}
                          inputProps={mergeNumberInputProps({
                            min: OPTIONS_MIN_VALID_M_ALPHA_MIN,
                            max: OPTIONS_MIN_VALID_M_ALPHA_MAX,
                            step: 'any',
                          })}
                          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                        />
                      </Box>
                    </>
                  )}
                </Stack>
              </Paper>
              <Paper
                variant="outlined"
                sx={{
                  p: 1,
                  width: '100%',
                  minWidth: 0,
                  flex: { xs: '1 1 auto', lg: '1 1 0' },
                  position: { lg: 'sticky' },
                  top: { lg: 16 },
                  alignSelf: 'flex-start',
                }}
              >
                <SlopeStabilitySketch
                  input={calculationInput}
                  mode={mode}
                  criticalSurface={showOutput ? critical : null}
                  searchHeatmap={showOutput ? searchHeatmap : null}
                  heatmapPalette={heatmapPalette}
                />
              </Paper>
            </Stack>

            <div className="buttonContainer">
              <CalculateButton
                calculationCountReached={calculationCountReached}
                calculate={calculate}
                calculationInput={calculationInput}
                URL={URL}
                calculateButtonDisabled={calculateButtonDisabled}
                isLoadingCalculate={isLoadingCalculate}
                isLoadingLoad={isLoadingLoad}
                isLoadingSave={isLoadingSave}
              />
              <SaveButton
                saveCountReached={saveCountReached}
                checkCalculationNameforSaving={checkCalculationNameforSaving}
                calculationInput={calculationInput}
                URL={URL}
                saveButtonDisabled={saveButtonDisabled}
                isLoadingCalculate={isLoadingCalculate}
                isLoadingLoad={isLoadingLoad}
                isLoadingSave={isLoadingSave}
              />
            </div>

            {isLoadingLoad && <CircularProgress size={30} />}
            {isLoadingCalculate && (
              <div className="centerLoading">
                <CircularProgress />
              </div>
            )}

            {showOutput && (
              <>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 0,
                    overflow: 'hidden',
                    borderRadius: 2,
                    borderColor: 'divider',
                    bgcolor: resultTone.paperBg,
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1.4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      background: resultTone.headerGradient,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Result Summary
                    </Typography>
                    <Box
                      sx={{
                        px: 1.1,
                        py: 0.35,
                        borderRadius: 1,
                        bgcolor: resultTone.fsBadgeBg,
                        border: `1px solid ${resultTone.fsBadgeBorder}`,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700, color: resultTone.fsBadgeText }}>
                        Critical FS {fixed(critical?.factorOfSafety, 6)}
                      </Typography>
                    </Box>
                  </Box>

                  <Stack spacing={2} sx={{ p: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1 }}>
                        Critical Surface
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1 }}>
                        {summaryCriticalCards.map((card) => (
                          <Box
                            key={`critical-${card.label}`}
                            sx={{
                              ...summaryCardBaseSx,
                              bgcolor: resultTone.criticalCardBg,
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              {card.label}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {card.value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1 }}>
                        Search Diagnostics
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 1 }}>
                        {summaryDiagnosticsCards.map((card) => (
                          <Box
                            key={`diag-${card.label}`}
                            sx={{
                              ...summaryCardBaseSx,
                              bgcolor: resultTone.diagnosticsCardBg,
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              {card.label}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {card.value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    {summarySurfaceFilterCards.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1 }}>
                          Active Surface Filters
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 1 }}>
                          {summarySurfaceFilterCards.map((card) => (
                            <Box
                              key={`filter-${card.label}`}
                              sx={{
                                ...summaryCardBaseSx,
                                bgcolor: resultTone.filterCardBg,
                                borderColor: resultTone.filterCardBorder,
                              }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                {card.label}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {card.value}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {shouldShowSliceDebugSection && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1 }}>
                          Slice Diagnostics
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Debug slices were enabled for this run. Values below are reported for the critical surface slices.
                        </Typography>

                        {criticalSliceDebug.length > 0 ? (
                          <>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                                gap: 1,
                                mt: 1,
                                mb: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  ...summaryCardBaseSx,
                                  bgcolor: resultTone.criticalCardBg,
                                }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  Slice Count
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {criticalSliceDebug.length}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  ...summaryCardBaseSx,
                                  bgcolor: resultTone.criticalCardBg,
                                }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  Min M-Alpha
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {Number.isFinite(minSliceDebugMAlpha) ? fixed(minSliceDebugMAlpha, 6) : '-'}
                                </Typography>
                              </Box>
                            </Box>

                            <TableContainer variant="outlined" sx={sliceDiagnosticsTableSx}>
                              <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Slice</TableCell>
                                    <TableCell align="right">Mid X (m)</TableCell>
                                    <TableCell align="right">Width (m)</TableCell>
                                    <TableCell align="right">Height (m)</TableCell>
                                    <TableCell align="right">Alpha (deg)</TableCell>
                                    <TableCell align="right">Weight</TableCell>
                                    <TableCell align="right">Pore u</TableCell>
                                    <TableCell align="right">Base N</TableCell>
                                    <TableCell align="right">Shear</TableCell>
                                    <TableCell align="right">M-Alpha</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {criticalSliceDebug.map((row) => (
                                    <TableRow key={`slice-debug-row-${row.sliceIndex}`}>
                                      <TableCell>{row.sliceIndex}</TableCell>
                                      <TableCell align="right">{fixed(row.midX, 3)}</TableCell>
                                      <TableCell align="right">{fixed(row.width, 3)}</TableCell>
                                      <TableCell align="right">{fixed(row.height, 3)}</TableCell>
                                      <TableCell align="right">{fixed(row.signedAlphaDeg, 3)}</TableCell>
                                      <TableCell align="right">{fixed(row.weight, 3)}</TableCell>
                                      <TableCell align="right">{fixed(row.porePressure, 3)}</TableCell>
                                      <TableCell align="right">{fixed(row.baseNormalForce, 3)}</TableCell>
                                      <TableCell align="right">{fixed(row.baseShearForce, 3)}</TableCell>
                                      <TableCell align="right">{fixed(row.mAlpha, 6)}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </>
                        ) : (
                          <Alert severity="info" sx={{ mt: 1 }}>
                            Debug slices were requested, but no slice diagnostics were returned for the critical surface.
                          </Alert>
                        )}
                      </Box>
                    )}

                    {Array.isArray(output?.warnings) && output.warnings.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1 }}>
                          Analysis Notes
                        </Typography>
                        <Stack spacing={0.75}>
                          {output.warnings.map((warningText) => (
                            <Alert key={warningText} severity="info" sx={{ py: 0.5 }}>
                              {warningText}
                            </Alert>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              </>
            )}
          </Stack>
        </div>
      </div>
      <Footer />
    </>
  );
};

const SlopeStabilityWithProvider = () => (
  <CalculationContextProvider>
    <SlopeStability />
  </CalculationContextProvider>
);

export default SlopeStabilityWithProvider;
